<template>
    <div class="pt-8 flex flex-col lg:flex-row p-4 min-h-screen bg-light-bg dark:bg-dark-bg dark:text-dark-text">
      <AppSidebar />
  
      <div class="flex flex-col flex-grow p-6 lg:p-12 space-y-4 h-full">
        <!-- Wiadomość sukcesu lub błędu -->
      <div v-if="message" :class="messageType" 
          class="fixed top-0 left-0 right-0 z-5 p-4 rounded-lg mb-4 text-center 
                  bg-white dark:bg-dark-box-bg dark:text-dark-text dark:border 
                  dark:border-dark-border">
          {{ message }}
          <div class="absolute bottom-0 left-0 h-1 rounded-lg" 
              :style="{ width: progress + '%', backgroundColor: getProgressColor(), borderRadius: '8px' }">
          </div>
      </div>
  
        <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4 space-y-4 lg:space-y-0">
          <div class="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <h1 class="text-xl md:text-2xl font-bold">Rozliczenia</h1>
            
            <!-- Przełącznik typu rozliczeń -->
            <div class="flex items-center space-x-4">
              <span class="text-sm font-medium text-gray-700 dark:text-dark-text">
                Rozliczenia Kierowcy
              </span>
              <label class="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  v-model="isCompanyPayoffs" 
                  @change="togglePayoffType"
                  class="sr-only peer"
                >
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600"></div>
              </label>
              <span class="text-sm font-medium text-gray-700 dark:text-dark-text">
                Rozliczenia Firmy
              </span>
            </div>
          </div>
        </div>
  
        <div class="bg-white p-4 rounded-lg shadow-md w-full h-auto overflow-visible dark:bg-dark-box-bg dark:border dark:border-dark-border">
          <div class="flex flex-col md:flex-row items-center justify-between mb-4 space-y-4 md:space-y-0">
            <h2 class="text-lg font-semibold text-gray-700 dark:text-dark-text">
              {{ isCompanyPayoffs ? 'Lista Rozliczeń Firmy' : 'Lista Rozliczeń Kierowców' }}
            </h2>
            
            <!-- Statystyki -->
            <div class="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm" v-if="!isCompanyPayoffs">
              <div class="bg-teal-50 dark:bg-teal-900/20 px-3 py-1 rounded-full">
                <span class="text-teal-700 dark:text-teal-300">
                  Łącznie: {{ totalKilometers }} km
                </span>
              </div>
              <div class="bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                <span class="text-blue-700 dark:text-blue-300">
                  Średnia: {{ averageKilometers }} km
                </span>
              </div>
            </div>
          </div>
  
          <div class="w-full mb-4">
            <DynamicSearch
                :data="payoffs"
                :keysToSearch="searchKeys"
                @search-results="handleSearchResults"
                @clear-search="clearSearch"
                class="mt-4 dark:bg-dark-box-bg text-gray-900 dark:text-dark-text w-full md:w-auto"
                :placeholder="isCompanyPayoffs ? 'Szukaj w rozliczeniach firmy...' : 'Szukaj w rozliczeniach kierowców...'"
            />
          </div>
  
          <!-- Loading state -->
          <div v-if="isLoading" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <span class="ml-2 text-gray-600 dark:text-gray-400">Ładowanie rozliczeń...</span>
          </div>
  
          <!-- Error state -->
          <div v-else-if="error" class="text-center py-8">
            <div class="text-red-500 mb-2">{{ error }}</div>
            <button 
              @click="fetchPayoffs" 
              class="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Spróbuj ponownie
            </button>
          </div>
  
          <!-- Tabela rozliczeń -->
          <div v-else class="overflow-x-auto w-full">
            <table class="min-w-full bg-white border border-gray-300 rounded-lg dark:bg-dark-table-bg dark:border-dark-border">
              <thead>
              <tr class="bg-gray-50 dark:bg-dark-table-header dark:text-dark-text">
                <th v-if="!isCompanyPayoffs" class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    @click="sortPayoffs('firstName')">
                  Imię
                  <span class="ml-1 text-xs">{{ getSortIcon('firstName') }}</span>
                </th>
                <th v-if="!isCompanyPayoffs" class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    @click="sortPayoffs('lastName')">
                  Nazwisko
                  <span class="ml-1 text-xs">{{ getSortIcon('lastName') }}</span>
                </th>
                <th v-if="!isCompanyPayoffs" class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    @click="sortPayoffs('kilometers')">
                  Km z Apki
                  <span class="ml-1 text-xs">{{ getSortIcon('kilometers') }}</span>
                </th>
                <th v-if="isCompanyPayoffs" class="py-3 px-4 border-b border-gray-300 dark:border-dark-border text-left text-sm font-semibold text-gray-700 dark:text-dark-text cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    @click="sortPayoffs('companyName')">
                  Nazwa Firmy
                  <span class="ml-1 text-xs">{{ getSortIcon('companyName') }}</span>
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="filteredPayoffs.length === 0">
                <td :colspan="isCompanyPayoffs ? 1 : 3" class="text-center py-4 text-red-500 font-semibold dark:text-dark-status-unavailable">
                  {{ isCompanyPayoffs ? 'Brak rozliczeń firmy' : 'Brak rozliczeń kierowców' }}
                </td>
              </tr>
              <tr v-else 
                  v-for="(payoff) in paginatedPayoffs" 
                  :key="payoff.id" 
                  class="border-b border-gray-300 dark:border-dark-border hover:bg-light-hover dark:hover:bg-dark-hover hover:cursor-pointer transition-colors"
                  @click="goToProfile(payoff)"
              >
                <td v-if="!isCompanyPayoffs" class="py-3 px-4 dark:text-dark-text">{{ getDriverFirstName(payoff) }}</td>
                <td v-if="!isCompanyPayoffs" class="py-3 px-4 dark:text-dark-text">{{ getDriverLastName(payoff) }}</td>
                <td v-if="!isCompanyPayoffs" class="py-3 px-4 dark:text-dark-text">
                  <span class="font-medium">{{ getKilometers(payoff) }}</span>
                </td>
                <td v-if="isCompanyPayoffs" class="py-3 px-4 dark:text-dark-text">
                  {{ getCompanyName(payoff) }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Paginacja -->
          <div class="mt-4 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div class="text-center sm:text-left text-sm text-light-text dark:text-gray-400">
              Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedPayoffs.length }} z {{ filteredPayoffs.length }} 
              {{ isCompanyPayoffs ? 'rozliczeń firmy' : 'rozliczeń kierowców' }}
            </div>
  
            <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
              <button v-if="currentPage > 1"
                      @click="goToPage(1)"
                      class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Pierwsza strona
              </button>
  
              <button v-if="currentPage > 1"
                      @click="prevPage"
                      class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                &laquo; Poprzednia
              </button>
  
              <button v-for="page in visiblePages"
                      :key="page"
                      @click="goToPage(page)"
                      :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text hover:bg-gray-300 dark:hover:bg-gray-600']"
                      class="px-3 py-1 rounded-lg text-sm cursor-pointer transition-colors">
                {{ page }}
              </button>
  
              <button v-if="currentPage < totalPages"
                      @click="nextPage"
                      class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Następna &raquo;
              </button>
  
              <button v-if="currentPage < totalPages"
                      @click="goToPage(totalPages)"
                      class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Ostatnia strona
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
<script>
    import {useAuthStore} from '@/stores/authStore';
    import AppSidebar from '@/components/AppSidebar.vue';
    import DynamicSearch from '../search/DynamicSearch.vue';
    import payoffService from '@/services/payoffService';
    import { useSessionStore } from '@/stores/useSessionStore';
    import companiesService from '@/services/companiesService';
  
  export default {
    name: "PayoffPageDispatcher",
    components: {
      AppSidebar,
      DynamicSearch,
    },
    setup() {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();
      if (authStore.isAuthenticated) {
        authStore.checkExpiration();
      }
      return {user: authStore.user, sessionStore};
    },
    data() {
      return {
        message: '',
        messageType: '',
        progress: 0,
        payoffs: [],
        filteredPayoffs: [],
        isLoading: false,
        error: null,
        
        // Nowe właściwości dla przełącznika
        isCompanyPayoffs: false, // false = rozliczenia kierowcy, true = rozliczenia firmy
        
        // Sortowanie
        sortField: '',
        sortDirection: 'asc',
  
        // Paginacja
        currentPage: 1,
        itemsPerPage: 15,
        totalPages: 0,
        visiblePages: [],
        isSearching: false,
      };
    },
    computed: {
      paginatedPayoffs() {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredPayoffs.slice(start, end);
      },
      
      totalKilometers() {
        return this.filteredPayoffs.reduce((sum, payoff) => 
          sum + this.getKilometersValue(payoff), 0
        ).toFixed(2);
      },
      
      averageKilometers() {
        if (this.filteredPayoffs.length === 0) return '0.00';
        return (this.totalKilometers / this.filteredPayoffs.length).toFixed(2);
      },
      
      // Dynamiczne klucze wyszukiwania w zależności od typu rozliczeń
      searchKeys() {
        if (this.isCompanyPayoffs) {
          return ['companyName', 'name']; // dla firm
        } else {
          return ['driver.firstName', 'driver.lastName']; // dla kierowców
        }
      }
    },
    watch: {
      isCompanyPayoffs: {
        handler(newValue) {
          // Zapisz preferencję użytkownika w localStorage
          localStorage.setItem('payoffViewType', newValue ? 'company' : 'driver');
        }
      }
    },
    mounted() {
      this.calculateItemsPerPage();
      window.addEventListener("resize", this.calculateItemsPerPage);

      // Przywróć ostatnią preferencję użytkownika
      const savedViewType = localStorage.getItem('payoffViewType');
      if (savedViewType) {
        this.isCompanyPayoffs = savedViewType === 'company';
      }

      const pageFromUrl = parseInt(this.$route.query.currentPage) || 1;
      this.currentPage = pageFromUrl;

      this.fetchPayoffs().then(() => {
        this.calculateTotalPages();
      });

      this.refreshPayoffs();
    },
    beforeUnmount() {
      if (this.sessionStore.intervalId) {
        clearInterval(this.sessionStore.intervalId);
        this.sessionStore.intervalId = null;
      }
      window.removeEventListener("resize", this.calculateItemsPerPage);
    },
    methods: {
      // Bezpieczne metody dostępu do danych
      getDriverFirstName(payoff) {
        return payoff?.driver?.firstName || 'N/A';
      },
      
      getDriverLastName(payoff) {
        return payoff?.driver?.lastName || 'N/A';
      },
      
      getKilometers(payoff) {
        const orders = payoff?.driver?.orders || [];
        const totalKm = orders.reduce((sum, order) => {
          return sum + (order?.distanceInKm || 0);
        }, 0);
        return totalKm.toFixed(2);
      },

      getKilometersValue(payoff) {
        const orders = payoff?.driver?.orders || [];
        const totalKm = orders.reduce((sum, order) => {
          return sum + (order?.distanceInKm || 0);
        }, 0);
        return totalKm;
      },
      
      getCompanyName(payoff) {
        return payoff?.companyName || payoff?.name || 'N/A';
      },
      
      refreshPayoffs() {
        this.sessionStore.startInterval(() => {
          if (!this.isSearching) {
              this.fetchPayoffs();
          }
        }, 10000);
      },
      
      async togglePayoffType() {
        this.currentPage = 1;
        this.sortField = '';
        this.sortDirection = 'asc';
        // Wyczyść dane przed przełączeniem
        this.payoffs = [];
        this.filteredPayoffs = [];
        await this.fetchPayoffs();
      },
      
      async fetchPayoffs() {
        this.error = null;
        
        try {
          let response;
          if (this.isCompanyPayoffs) {
            // Wywołanie dla rozliczeń firmy
            response = await companiesService.getAllCompanies();
          } else {
            // Wywołanie dla rozliczeń kierowców
            response = await payoffService.getAllPayoffs();
          }
          
          this.payoffs = response.data || [];
          this.filteredPayoffs = [...this.payoffs];
          this.calculateTotalPages();
        } catch (error) {
          console.error('Błąd podczas pobierania rozliczeń:', error);
          this.error = 'Nie udało się pobrać listy rozliczeń. Spróbuj ponownie.';
          this.showMessage('Błąd podczas pobierania danych', 'error');
          this.payoffs = [];
          this.filteredPayoffs = [];
        }
      },
      
      sortPayoffs(field) {
        if (this.sortField === field) {
          this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortField = field;
          this.sortDirection = 'asc';
        }
        
        this.filteredPayoffs.sort((a, b) => {
          let aVal, bVal;
          
          switch(field) {
            case 'firstName':
              aVal = this.getDriverFirstName(a).toLowerCase();
              bVal = this.getDriverFirstName(b).toLowerCase();
              break;
            case 'lastName':
              aVal = this.getDriverLastName(a).toLowerCase();
              bVal = this.getDriverLastName(b).toLowerCase();
              break;
            case 'kilometers':
              aVal = this.getKilometersValue(a);
              bVal = this.getKilometersValue(b);
              break;
            case 'companyName':
              aVal = this.getCompanyName(a).toLowerCase();
              bVal = this.getCompanyName(b).toLowerCase();
              break;
            default:
              return 0;
          }
          
          if (this.sortDirection === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
        
        this.currentPage = 1;
        this.calculateTotalPages();
      },
      
      getSortIcon(field) {
        if (this.sortField !== field) return '↕️';
        return this.sortDirection === 'asc' ? '↑' : '↓';
      },
      
      formatCurrency(amount) {
        return new Intl.NumberFormat('pl-PL', {
          style: 'currency',
          currency: 'PLN'
        }).format(amount);
      },
      
      calculateItemsPerPage() {
        const width = window.innerWidth;

        if (width >= 3840) {
          this.itemsPerPage = 24;
        } else if (width >= 2560) {
          this.itemsPerPage = 15;
        } else if (width >= 1920) {
          this.itemsPerPage = 8;
        } else {
          this.itemsPerPage = 5;
        }

        this.calculateTotalPages();
      },
      
      calculateTotalPages() {
        this.totalPages = Math.ceil(this.filteredPayoffs.length / this.itemsPerPage) || 1;
        this.updateVisiblePages();
      },
      
      updateVisiblePages() {
        const range = 5;
        const start = Math.max(1, this.currentPage - Math.floor(range / 2));
        const end = Math.min(start + range - 1, this.totalPages);
  
        this.visiblePages = Array.from({length: end - start + 1}, (_, i) => start + i);
      },
      
      goToPage(page) {
        this.currentPage = page;
        this.updateVisiblePages();
      },
      
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.updateVisiblePages();
        }
      },
      
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.updateVisiblePages();
        }
      },
      
      showMessage(msg, type) {
        this.message = msg;
        this.messageType = type === 'success' ? 'success-message' : 'error-message';
        this.progress = 0;
  
        const interval = 30;
        const duration = 5000;
        const step = 100 / (duration / interval);
  
        const progressInterval = setInterval(() => {
          this.progress += step;
          if (this.progress >= 100) {
            clearInterval(progressInterval);
            this.progress = 0;
            this.message = '';
            this.messageType = '';
          }
        }, interval);
      },
      
      getProgressColor() {
        return this.messageType === 'success-message' ? '#16a34a' : '#b91c1c';
      },
      
      handleSearchResults(results) {
        this.filteredPayoffs = results;
        this.currentPage = 1;
        this.calculateTotalPages();

        this.isSearching = results.length > 0;

        this.goToPage(1);
      },

      clearSearch() {
        this.isSearching = false;

        this.goToPage(1);
      },
      
      goToProfile(payoff) {
        const currentPage = this.currentPage;
        const viewType = this.isCompanyPayoffs ? 'company' : 'driver';
        const payoffId = payoff?.payoffId || payoff?.id;

        if (viewType === 'company') {
          if (payoffId && this.user.role === 'ROLE_ADMIN') {
            this.$router.push({
              name: 'PayoffCompanyProfileAdmin', 
              params: {id: payoffId}, 
              query: {
                currentPage: currentPage,
              }
            });
          } else if (payoffId) {
            this.$router.push({
              name: 'PayoffCompanyProfileDispatcher', 
              params: {id: payoffId},
              query: {
                currentPage: currentPage,
              }
            });
          }
        } else {
          if (payoffId && this.user.role === 'ROLE_ADMIN') {
            this.$router.push({
              name: 'PayoffProfileAdmin', 
              params: {id: payoffId}, 
              query: {
                currentPage: currentPage,
              }
            });
          } else if (payoffId) {
            this.$router.push({
              name: 'PayoffProfileDispatcher', 
              params: {id: payoffId},
              query: {
                currentPage: currentPage,
              }
            });
          }
        }
      }
    }
  };
  </script>